import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { Button } from '@ipc/ipc-components';
import ListTable from '../../../../common/list-table/components/ListTable';
import {
  tableCheckItemsSetSuccess, tableLoadData,
} from '../../../../common/list-table/actions/ListTableActions';
import { blockRoutingAction, triggerConfirmationDialogAction, triggerRedirect } from '../../../../common/redux/actions/redirectActions';
import {
  getRefresh,
} from '../../../../common/list-table/reducers/selectors/ListTableSelectors';
import { putDenestImpcMappingDeactivateRequestActionCreator } from '../redux/actions/DenestImpcMappingActions';
import TableDefinitions from '../../../../common/TableDefinitions';
import {
  getData, getIsLoading, getCheckboxSelections, getSaveRequestResponse,
} from '../redux/selectors/DenestImpcMappingSelectors';
import { getShowConfirmationDialog } from '../../../../common/redux/selectors/redirectSelector';
import { DenestImpcMappingStyle } from '../component/DenestImpcMapping.styles';
import DeactivateDenestImpcMappingDialog from '../deactivate-denest-mapping/component/DeactivateDenestImpcMappingDialog';
import denestImpcMappingTableColumns from './DenestImpcMappingTableColumns';
import DenestImpcMappingAddDialog from '../add-denest-mapping/component/DenestImpcMappingAddDialog';
import ConfirmationDialog from '../../../../common/list-table/helpers/confirmationDialog';
import { getUserRole } from '../../../../common/user/redux/selectors/UserSelectors';
import { formatMessage } from '../../../../../locales/locales';

function DenestImpcMappingContainer(props) {
  const [showAddModal, setShowAddModal] = useState(false);
  const {
    isLoading,
    data,
    selections,
    tableLoad,
    saveRequest,
    forceRefresh,
    setLocalChanges,
    showConfirmationDialog,
    updateConfirmationDialog,
    userRole,
    confirmRedirect,
    saveRequestResponse,
  } = props;

  const populateCheckboxFunctions = (isRowValidCheck, checkedRecords) => ({
    selectAll: () => {
      const { checkItems } = props;
      const checkableList = isRowValidCheck ? data.filter((item) => !isRowValidCheck({ original: item })) : data;
      const isAllChecked = selections.length === checkableList.length;
      checkItems({
        selectedRows: isAllChecked ? [] : checkableList,
        tableDefinition: {
          id: TableDefinitions.MRD_DENEST_IMPC_MAPPING.id,
        },
      });
    },
    rowChecked: (row) => !!checkedRecords.find((item) => item.rowNumber === row.original.rowNumber),
    updateSelectedRecords: (row) => {
      const { checkItems } = props;
      const isChecked = checkedRecords.find((item) => item?.rowNumber === row.original.rowNumber);
      checkItems({
        selectedRows: isChecked ? checkedRecords.filter((item) => item.rowNumber !== row.original.rowNumber) : [...checkedRecords, data.find((item) => item.rowNumber === row.original.rowNumber)],
        tableDefinition: {
          id: TableDefinitions.MRD_DENEST_IMPC_MAPPING.id,
        },
      });
    },
  });

  const [deactivateVisible, setDeactivateVisible] = useState(false);
  const [localChanges, setChanges] = useState([]);

  const cancelHandler = () => {
    setChanges([]);
    setLocalChanges(false);
    if (showConfirmationDialog) {
      updateConfirmationDialog(false);
      confirmRedirect(true);
    }
  };

  useEffect(() => {
    if (forceRefresh || saveRequestResponse === 'success') {
      tableLoad(TableDefinitions.MRD_DENEST_IMPC_MAPPING.id);
      cancelHandler();
    }
  }, [forceRefresh, saveRequestResponse]);

  const deactivateButtonHandler = (isSubmitted) => {
    const { checkItems } = props;
    setDeactivateVisible(!deactivateVisible);
    if (isSubmitted) {
      checkItems({
        selectedRows: [],
        tableDefinition: {
          id: TableDefinitions.MRD_DENEST_IMPC_MAPPING.id,
        },
      });
    }
  };

  const clickAddButton = () => {
    setShowAddModal(true);
  };

  const hideAddFormModal = () => {
    setShowAddModal(false);
  };

  const confirmDeactivate = (incomingChanges) => {
    setChanges([...localChanges, ...incomingChanges]);
    setLocalChanges([...localChanges, ...incomingChanges].length > 0);
  };

  const onSave = () => {
    saveRequest(localChanges);
  };

  const tableData = data.map((record) => localChanges.find((item) => record.rowNumber === item.rowNumber) || record);
  const columns = denestImpcMappingTableColumns(data, populateCheckboxFunctions, selections, localChanges, userRole);

  return (
    <DenestImpcMappingStyle>
      <div className={`checkbox-${userRole === 'IPC_ADMIN'}`}>
        {userRole === 'IPC_ADMIN' && (
          <div className="impc-button-container">
            <Button
              disabled={selections.length > 0 || localChanges.length > 0}
              className={`primary-button-${selections.length > 0 || localChanges.length > 0} impc-button ADD`}
              id="add"
              onClick={clickAddButton}
            >
              ADD
            </Button>

            <Button
              disabled={selections.length === 0}
              className={`primary-button-${selections.length === 0} impc-button DEACTIVATE`}
              id="deactivate"
              onClick={() => deactivateButtonHandler(false)}
            >
              DEACTIVATE
            </Button>
            <Button
              disabled={localChanges.length === 0 || selections.length > 0}
              className={`primary-button-${localChanges.length === 0 || selections.length > 0} impc-button SAVE`}
              id="save"
              onClick={onSave}
            >
              SAVE
            </Button>
            <Button
              disabled={localChanges.length === 0}
              className={`primary-button-${localChanges.length === 0} impc-button CANCEL`}
              onClick={cancelHandler}
            >
              CANCEL
            </Button>
          </div>
        )}
        <ListTable
          listId={TableDefinitions.MRD_DENEST_IMPC_MAPPING.id}
          loading={isLoading}
          columns={columns}
          data={tableData}
          isDownloadEnable
          downloadFileAs="IMPC_MAPPING_REPORT"
          downloadFileTemplate="MRD_DENEST_IMPC_MAPPING"
        />
        {showAddModal && (
          <DenestImpcMappingAddDialog
            currentMapping={data}
            closeDialog={hideAddFormModal}
            openDialog={showAddModal}
          />
        )}
        <DeactivateDenestImpcMappingDialog
          openDialog={deactivateVisible}
          closeDialog={deactivateButtonHandler}
          selectedRecords={selections}
          confirmDeactivate={confirmDeactivate}
          tableData={tableData}
        />
        <ConfirmationDialog
          isOpen={showConfirmationDialog}
          submitButtonText="Yes"
          cancelButtonText="No"
          onClose={() => updateConfirmationDialog(false)}
          onSubmit={cancelHandler}
          confirmationTitle={formatMessage('en', 'impcmapping.deactive.unsaved.changes.warning.message')}
        />
      </div>
    </DenestImpcMappingStyle>
  );
}

function mapStateToProps(state) {
  return {
    data: getData(state),
    isLoading: getIsLoading(state),
    selections: getCheckboxSelections(state),
    showConfirmationDialog: getShowConfirmationDialog(state),
    forceRefresh: getRefresh(state, TableDefinitions.MRD_DENEST_IMPC_MAPPING.id),
    userRole: getUserRole(state),
    saveRequestResponse: getSaveRequestResponse(state),
  };
}
const mapDispatchToProps = (dispatch) => bindActionCreators({
  checkItems: tableCheckItemsSetSuccess,
  tableLoad: tableLoadData,
  saveRequest: putDenestImpcMappingDeactivateRequestActionCreator,
  setLocalChanges: blockRoutingAction,
  updateConfirmationDialog: triggerConfirmationDialogAction,
  confirmRedirect: triggerRedirect,
}, dispatch);

DenestImpcMappingContainer.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  checkItems: PropTypes.func,
  selections: PropTypes.arrayOf(PropTypes.object),
  tableLoad: PropTypes.func,
  saveRequest: PropTypes.func,
  setLocalChanges: PropTypes.func,
  showConfirmationDialog: PropTypes.bool,
  updateConfirmationDialog: PropTypes.func,
  confirmRedirect: PropTypes.func,
  forceRefresh: PropTypes.bool,
  userRole: PropTypes.string,
  saveRequestResponse: PropTypes.string,
};

DenestImpcMappingContainer.defaultProps = {
  selections: [],
};

export default connect(mapStateToProps, mapDispatchToProps)(DenestImpcMappingContainer);
